import { useCallback } from "react";

import {
    DotsLoader,
    ListItemSeparator,
    ListItemWebsite,
    AutocompleteWebsiteItem,
    type IAutocompleteProps,
} from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { useFirstLandModal } from "app/pages/activation/components/FirstLandModalProvider/context";
import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import type { IWebsiteRecent } from "app/services/solutions2Services/HomepageDataFetchers/HomepageDataFetcherTypes";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

import { useTranslation } from "../WithTranslation/src/I18n";

import { ScrollAreaWrap } from "./components/ScrollAreaWrapper";
import { MainSiteAutocompleteStyled, StyledTextField } from "./styles";
import type { IAutocompleteWebsitesMainItemProps, IMainItem, IWebsitePageResult } from "./types";
import { filterOutIllegalWebsiteQueryBarItems, getListItemsGroupsContent, isValidSiteItem } from "./utils";

const normalizedRecent = (item: IWebsiteRecent, showCompareSites = false) => {
    const itemData = item.data;
    if (showCompareSites && itemData.comparedItems.length > 0) {
        return {
            mainSite: {
                domain: itemData.mainItem,
                iconUrl: itemData.resources.mainItemFavIcon,
            },
            compareItems: itemData.comparedItems.map((comparedItem, index) => {
                return {
                    domain: comparedItem,
                    iconUrl: itemData.resources.comparedItemsFavicon[index],
                };
            }),
        };
    }
    return {
        name: itemData.mainItem,
        image: itemData.resources.mainItemFavIcon,
    };
};

export const AutocompleteWebsitesMainItem = ({
    onClick,
    recentSearches,
    recentsTitle = "autocomplete.websitesMain.seperator.text.recents",
    excludes = [],
    autocompleteProps,
    className,
    onKeyUp,
    onArrowKeyPress,
    overrideEnterFunc,
    onGetListItems,
    filterListItems,
    isDisabled,
    disablePlaceHolderKey = "websites.autocomplete.disable.placeholder",
    showCompareSites,
    children,
    emptyState,
}: IAutocompleteWebsitesMainItemProps) => {
    const translate = useTranslation();
    const { isOpen } = useFirstLandModal();

    const onItemClick = (item: IWebsitePageResult, type: "query" | "recent") => () => {
        onClick(item, type);
    };

    const renderItemCreator = (type: string) => (item: FixmeType) => ({
        ...item,
        type,
        props: { onClick: onItemClick(item, type as "query" | "recent") },
    });

    // Wraps the getListItems function, so that we could call it with
    // a "onGetListItems" callback, if the user provides such.
    // this allows us to get insights on whether the component
    // managed to receive any items from the query or not.
    const handleGetListItems = async (query: string) => {
        const listItems = await getListItems(query);

        onGetListItems?.(listItems);

        return filterListItems ? filterListItems(listItems) : listItems;
    };

    const getListItems = async (query?: string) => {
        if (query) {
            try {
                const results = await getWebsiteResults(query);
                if (!results.length) {
                    return [autocompleteStates.NO_DATA_SEARCHES];
                }
                const filteredResult = results.filter(isValidSiteItem(excludes)).map(renderItemCreator("query"));
                return filterOutIllegalWebsiteQueryBarItems(filteredResult);
            } catch (_e) {
                return [autocompleteStates.ERROR];
            }
        }

        if (recentSearches[0] === autocompleteStates.NO_DATA_RECENTS) {
            return [];
        }

        const recents = recentSearches
            .filter((x) => !excludes.find((exclude) => exclude.name === x.data["mainItem"]))
            .reduce((results, item) => {
                const { type } = item.data;

                if (type !== "website") {
                    return results;
                }

                const normalizedItem = normalizedRecent(item, showCompareSites);

                results.push(renderItemCreator("recent")(normalizedItem));

                return results;
            }, []);

        return recents;
    };

    const renderItems = useCallback(
        ({
            selectedItemId,
            listItems,
        }: // @ts-ignore FIXME: strict migration - IAutocompleteProps["renderItems"] may be undefined — extracting Parameters from optional function
        Parameters<IAutocompleteProps["renderItems"]>[0] & {
            listItems: Array<IMainItem | autocompleteStates>;
        }) => {
            if (!isOpen && emptyState && listItems.length === 0) {
                return emptyState;
            }

            if (!listItems.length) {
                return null;
            }

            const itemCreator = (item: IMainItem, index: number) => {
                const { name, image } = item;

                if (item.compareItems) {
                    return (
                        <AutocompleteWebsiteItem
                            key={`${item.mainSite.domain}_compare_${index}`}
                            maxCompareItemsToDisplay={1}
                            mainSite={item.mainSite}
                            compareSites={item.compareItems}
                            itemUrl={image}
                            onItemClick={() => item.props.onClick(item)}
                        />
                    );
                }
                return (
                    <ListItemWebsite
                        isActive={index === selectedItemId}
                        key={`${name}_${index}`}
                        img={image ?? ""}
                        text={name ?? ""}
                        onClick={item.props.onClick}
                    />
                );
            };

            const { recents, searchResults } = getListItemsGroupsContent({
                listItems,
                renderListItem: itemCreator,
            });

            return (
                <div className="ListItemsContainer">
                    <Stack gap="8px">
                        {children}
                        {recents.length > 0 ? (
                            <section>
                                <ListItemSeparator>{translate(recentsTitle)}</ListItemSeparator>
                                <ScrollAreaWrap>{recents}</ScrollAreaWrap>
                            </section>
                        ) : null}
                        {searchResults.length > 0 ? (
                            <section>
                                <ScrollAreaWrap>{searchResults}</ScrollAreaWrap>
                            </section>
                        ) : null}
                    </Stack>
                </div>
            );
        },
        [isOpen, children, emptyState, recentsTitle, translate],
    );

    return isDisabled ? (
        <StyledTextField
            disabled
            placeholder={translate(disablePlaceHolderKey)}
        />
    ) : (
        <MainSiteAutocompleteStyled
            isFocused
            autoFocus
            className={className}
            loadingComponent={<DotsLoader />}
            preventTruncateUnlessForced
            resetValueOnSelect
            floating
            debounce={250}
            maxResults={20}
            onKeyUp={onKeyUp}
            onArrowKeyPress={onArrowKeyPress}
            overrideEnterFunc={overrideEnterFunc}
            initialSelectedItemId={-1}
            getListItems={handleGetListItems}
            renderItems={renderItems}
            placeholder={translate("autocomplete.websites.main.item.placeholder")}
            {...autocompleteProps}
        />
    );
};
